(function () {
	'use strict';

	class W4D_ProductCardCarousel {
		constructor() {
			this.facadeBound = false;
			this.imageObserverBound = false;
			this.lcpCompleted = false;
			this.idleQueue = [];
			this.isProcessingQueue = false;
			this.observer = null;

			// Carousels that have had their write-phase (prepareCarousel) done and are
			// waiting for their read/measure-phase (swiperEl.initialize()). Batched so
			// N carousels becoming "due" around the same time cost one forced layout
			// instead of N.
			this.pendingMounts = [];
			this.mountScheduled = false;

			this.init();
		}

		static initSwiper() {
			if (window.W4D_CarouselInstance) {
				window.W4D_CarouselInstance.init();
			} else {
				window.W4D_CarouselInstance = new W4D_ProductCardCarousel();
				window.W4D_ProductCardCarousel = window.W4D_CarouselInstance;
			}
		}

		init() {
			this.setupInteractionFacade();
			this.bindImageLoadObserver();

			// A re-init after the first LCP (filters, pagination, cart re-rendered the cards) mounts the new cards
			// right away; waiting for LCP again would never fire.
			if (this.lcpCompleted) {
				this.mountVisibleCards();
			} else {
				this.waitForLCP(() => this.onLCP());
			}
		}

		setupInteractionFacade() {
			if (this.facadeBound) return;
			this.facadeBound = true;

			const initCardCarousel = (card) => {
				if (!card) return;
				const carousel = card.querySelector('.product-card-carousel');
				if (!carousel) return;

				if (
					carousel.swiper ||
					carousel.classList.contains('is-initialized') ||
					carousel.getAttribute('data-carousel-initializing') === 'true'
				) {
					return;
				}

				carousel.setAttribute('data-carousel-initializing', 'true');
				// Write phase now, read phase deferred+batched - see scheduleMount().
				this.prepareCarousel(carousel);
				this.scheduleMount(carousel);
			};

			const handleEvent = (e) => {
				const card = e.target.closest ? e.target.closest('.product-card') : null;
				if (card) initCardCarousel(card);
			};

			// Capture phase, bubbling event types only (pointerover covers hover-enter,
			// same effect jQuery's mouseenter/pointerenter delegation was simulating).
			['pointerdown', 'touchstart', 'pointerover', 'focusin'].forEach((type) => {
				document.addEventListener(type, handleEvent, { capture: true, passive: true });
			});
		}

		bindImageLoadObserver() {
			if (this.imageObserverBound) return;
			this.imageObserverBound = true;

			// Global capture listener for any product image loading, to trigger blur-up fade-in.
			document.addEventListener(
				'load',
				(e) => {
					if (e.target && e.target.tagName === 'IMG') {
						const blurContainer = e.target.closest('.blur-img');
						if (blurContainer && !blurContainer.classList.contains('blur-img--loaded')) {
							blurContainer.classList.add('blur-img--loaded');
						}
					}
				},
				true
			);

			// Check already-completed images without forcing reflow.
			document.querySelectorAll('.blur-img:not(.blur-img--loaded) img').forEach((img) => {
				if (img.complete) {
					const blurContainer = img.closest('.blur-img');
					requestAnimationFrame(() => {
						blurContainer.classList.add('blur-img--loaded');
					});
				}
			});
		}

		waitForLCP(callback) {
			let triggered = false;
			let observer = null;

			const trigger = () => {
				if (triggered) return;
				triggered = true;
				if (observer) {
					try {
						observer.disconnect();
					} catch (e) {}
				}
				if ('requestIdleCallback' in window) {
					requestIdleCallback(callback, { timeout: 2000 });
				} else {
					setTimeout(callback, 200);
				}
			};

			if ('PerformanceObserver' in window) {
				try {
					observer = new PerformanceObserver((entryList) => {
						if (entryList.getEntries().length > 0) trigger();
					});
					observer.observe({ type: 'largest-contentful-paint', buffered: true });
				} catch (e) {
					observer = null;
				}
			}

			if (document.readyState === 'complete') {
				setTimeout(trigger, 100);
			} else {
				window.addEventListener('load', () => setTimeout(trigger, 100), { once: true });
			}

			// Safety fallback timeout (3s)
			setTimeout(trigger, 3000);
		}

		onLCP() {
			if (this.lcpCompleted) return;
			this.lcpCompleted = true;

			this.mountVisibleCards();
		}

		mountVisibleCards() {
			this.setupDeferredObserver();
			this.initStaticCards();
			this.processIdleQueue();
		}

		initStaticCards() {
			document.querySelectorAll('.product-card__media').forEach((mediaEl) => {
				const staticBlur = mediaEl.querySelector(':scope > .blur-img');
				if (!staticBlur || staticBlur.classList.contains('blur-img--loaded')) return;

				// Near the viewport only (same observer as the carousels), so off-screen cards keep their ThumbHash
				// placeholder until they are actually needed and aren't all decoded up front.
				if (this.observer) {
					this.observer.observe(staticBlur);
				} else {
					this.idleQueue.push({ type: 'static', el: staticBlur });
				}
			});
		}

		setupDeferredObserver() {
			if (!('IntersectionObserver' in window)) return;

			if (this.observer) {
				this.observer.disconnect();
			}

			this.observer = new IntersectionObserver(
				(entries, observer) => {
					// This callback only enqueues - it never triggers prepareCarousel/
					// initialize() itself. Doing that synchronously here is what was
					// forcing a reflow: it wrote to the DOM (classList/attrs/params) and
					// then, in the same task, made Swiper read layout to size its slides,
					// with no paint in between to let the browser batch it naturally.
					let queued = false;
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							const el = entry.target;
							observer.unobserve(el);
							if (el.classList.contains('blur-img')) {
								if (!el.classList.contains('blur-img--loaded')) {
									this.idleQueue.push({ type: 'static', el });
									queued = true;
								}
							} else if (!el.swiper && !el.classList.contains('is-initialized')) {
								this.idleQueue.push({ type: 'carousel', el });
								queued = true;
							}
						}
					});
					if (queued) this.processIdleQueue();
				},
				{ rootMargin: '300px 0px' }
			);

			document.querySelectorAll('.product-card-carousel').forEach((el) => {
				if (!el.swiper && !el.classList.contains('is-initialized')) {
					this.observer.observe(el);
				}
			});
		}

		scheduleNextIdleSlice(callback) {
			const cb = callback || ((deadline) => this.processIdleQueue(deadline));

			if ('requestIdleCallback' in window) {
				requestIdleCallback(cb, { timeout: 1000 });
			} else {
				setTimeout(() => {
					cb({
						timeRemaining: () => 15,
						didTimeout: true,
					});
				}, 16);
			}
		}

		async processIdleQueue(deadline) {
			if (this.isProcessingQueue || !this.idleQueue.length) return;
			this.isProcessingQueue = true;

			while (this.idleQueue.length > 0) {
				const item = this.idleQueue.shift();

				if (item.type === 'carousel') {
					const swiperEl = item.el;
					if (
						!swiperEl.swiper &&
						!swiperEl.classList.contains('is-initialized') &&
						swiperEl.getAttribute('data-carousel-initializing') !== 'true'
					) {
						swiperEl.setAttribute('data-carousel-initializing', 'true');
						this.prepareCarousel(swiperEl);
						this.scheduleMount(swiperEl);
					}
				} else if (item.type === 'static') {
					this.loadSlideImage(item.el);
				}

				// Feature-detect scheduler.yield with fallback.
				if ('scheduler' in window && typeof window.scheduler.yield === 'function') {
					await window.scheduler.yield();
				} else {
					await new Promise((resolve) => setTimeout(resolve, 0));
				}

				// Process at most 1 carousel per idle frame / 16ms budget, yielding to browser.
				if (deadline && typeof deadline.timeRemaining === 'function' && deadline.timeRemaining() < 5) {
					break;
				} else if (item.type === 'carousel') {
					break;
				}
			}

			this.isProcessingQueue = false;

			if (this.idleQueue.length > 0) {
				this.scheduleNextIdleSlice((newDeadline) => this.processIdleQueue(newDeadline));
			}
		}

		/**
		 * WRITE phase only: classList/attribute changes and assigning Swiper params
		 * onto the element. No layout-reading call happens in here, so any number of
		 * carousels can go through this back-to-back without forcing a reflow.
		 */
		prepareCarousel(swiperEl) {
			if (swiperEl.swiper) {
				swiperEl.swiper.destroy(true, true);
			}

			swiperEl.classList.add('is-initialized');
			swiperEl.removeAttribute('init');
			swiperEl.removeAttribute('data-carousel-initializing');

			const _self = this;

			const swiperParams = {
				slidesPerView: 1,
				slidesPerGroup: 1,
				spaceBetween: 0,
				speed: 300,
				nested: true, // Inside a product carousel the swipe on the image must not move that carousel too
				longSwipes: true,
				longSwipesRatio: 0.2,
				followFinger: true,
				touchAngle: 45,
				touchMoveStopPropagation: true,
				touchRatio: 0.8,
				threshold: 5,
				resistance: true,
				resistanceRatio: 0,
				// observer / observeParents stay at their default (false) - a
				// MutationObserver re-measuring on every DOM change would be another
				// source of forced-layout churn. resizeObserver stays at its default
				// (true): the grid column width also changes without a window resize
				// (collapsing the filter sidebar, the .shop-archive container query),
				// and Swiper writes slide widths inline in px.
				lazyPreload: false, // we already own image loading (loadSlideImage/blur-up); per Swiper's own docs, its built-in lazy handling attaches its own load listener and does extra layout work per loaded image otherwise
				navigation: {
					nextEl: swiperEl.querySelector('.product-card-carousel__nav--next'),
					prevEl: swiperEl.querySelector('.product-card-carousel__nav--prev'),
				},
				pagination: {
					el: swiperEl.querySelector('.product-card-carousel__dots'),
					clickable: true,
				},
				on: {
					init: function () {
						requestAnimationFrame(() => {
							_self.loadSlideImage(this.slides[this.activeIndex]?.querySelector('.blur-img'));
							_self.preloadAdjacent(this);
							_self.updateLinks(this);
						});
					},
					slideChange: function () {
						_self.loadSlideImage(this.slides[this.activeIndex]?.querySelector('.blur-img'));
						_self.preloadAdjacent(this);
						_self.updateLinks(this);
					},
				},
			};

			Object.assign(swiperEl, swiperParams);
		}

		/**
		 * READ/measure phase: swiperEl.initialize() is where Swiper actually reads
		 * geometry to size its slides. Every carousel queued via this method in the
		 * same animation frame gets initialized together in one requestAnimationFrame
		 * callback, so the browser flushes layout once for the whole batch instead of
		 * once per carousel.
		 */
		scheduleMount(swiperEl) {
			this.pendingMounts.push(swiperEl);
			if (this.mountScheduled) return;
			this.mountScheduled = true;

			requestAnimationFrame(() => {
				const batch = this.pendingMounts;
				this.pendingMounts = [];
				this.mountScheduled = false;

				batch.forEach((el) => {
					// Card could've left the DOM since it was queued (filtered/re-rendered grid).
					if (!el.isConnected) return;
					el.initialize();
				});
			});
		}

		preloadAdjacent(swiperInstance) {
			const nextSlide = swiperInstance.slides[swiperInstance.activeIndex + 1];
			const prevSlide = swiperInstance.slides[swiperInstance.activeIndex - 1];
			if (nextSlide) this.loadSlideImage(nextSlide.querySelector('.blur-img'));
			if (prevSlide) this.loadSlideImage(prevSlide.querySelector('.blur-img'));
		}

		updateLinks(swiper) {
			const activeIndex = swiper.activeIndex;
			// Swiper Element: swiper.el sits inside the shadow root, so .closest() can't reach the card from it.
			// swiper.hostEl is the light-DOM <swiper-container>.
			const card = (swiper.hostEl || swiper.el).closest('.product-card');
			if (!card) return;

			card.querySelectorAll('.product-card__link, .product-card__add-to-card').forEach((linkEl) => {
				const href = linkEl.getAttribute('href');
				if (!href) return;

				try {
					const url = new URL(href, window.location.origin);
					if (activeIndex > 0) {
						url.searchParams.set('imgIndex', activeIndex);
					} else {
						url.searchParams.delete('imgIndex');
					}
					linkEl.href = url.toString();
				} catch (e) {}
			});
		}

		loadSlideImage(slideEl) {
			this.renderSlidePlaceholder(slideEl);

			if (!slideEl || slideEl.classList.contains('blur-img--loaded')) return;

			const img = slideEl.querySelector('img');
			if (!img) return;

			const dataSrc = img.getAttribute('data-src');
			const currentSrc = img.getAttribute('src');
			const finalSrc = dataSrc || currentSrc;

			if (!finalSrc) return;

			const done = () => slideEl.classList.add('blur-img--loaded');

			if (dataSrc && currentSrc !== dataSrc) {
				img.setAttribute('src', dataSrc);
			}

			// No timer fallback: it marked lazy images below the fold as loaded before they arrived, hiding the
			// ThumbHash placeholder early. load/error (or complete) always settle it.
			if (img.complete) {
				requestAnimationFrame(done);
			} else {
				img.addEventListener('load', done, { once: true });
				img.addEventListener('error', done, { once: true });
			}
		}

		renderSlidePlaceholder(slideEl) {
			if (!slideEl || !window.W4D_ThumbHashCanvas) return;

			slideEl.querySelectorAll('.thumbhash-canvas').forEach((canvas) => {
				window.W4D_ThumbHashCanvas.render(canvas);
			});
		}
	}

	const boot = () => {
		window.W4D_CarouselInstance = new W4D_ProductCardCarousel();
		window.W4D_ProductCardCarousel = window.W4D_CarouselInstance;
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot);
	} else {
		boot();
	}
})();
